import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowSquareOut } from 'shared/icons';
import {
    CaseFiscalAction,
    CaseFiscalDocText,
    MlClaimMessagesUrl,
    WithdrawalCase,
    WithdrawalTitleParts,
} from '../components/pedidos/pedidosShared';
import {
    BuildInspectionFormData,
    InspectionDoneCard,
    InspectionScreen,
    InspectionWizard,
} from '../components/rma/inspectionStation';
import {
    CaseDueInfo,
    ComputeTracks,
    Fact,
    fmtDate,
    HasPhysicalFlow,
    InspectionChecklist,
    InspectionPhotoGroups,
    IsAwaitingWithdrawal,
    MediationChat,
    MlClaimUrl,
    MOVEMENT_STATUS_LABELS,
    Panel,
    PipelineStepper,
    PrintRmaLabels,
    REFUND_LABELS,
    RESOLUTION_LABELS,
    RESPONSIBLE_LABELS,
    RecontactOurTurn,
    ResolveModal,
    rmaIdentifier,
    SituationLine,
    STATUS_META,
    StatusPill,
    SUPPLIER_OUTCOME_LABELS,
    TrackChips,
    timeAgo,
} from '../components/rma/rmaShared';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import { useToast } from '../components/ui/Toast';
import {
    ML_ACTION_LABELS,
    ML_BENEFITED_LABELS,
    ML_CLAIM_STAGE_LABELS,
    ML_CLAIM_TYPE_LABELS,
    ML_CLOSED_BY_LABELS,
    ML_INVOICE_STATUS_LABELS,
    ML_LOGISTIC_TYPE_LABELS,
    ML_PRODUCT_CONDITION_LABELS,
    ML_PRODUCT_DESTINATION_LABELS,
    ML_RESOLUTION_REASON_LABELS,
    ML_RETURN_STATUS_LABELS,
    ML_REVIEW_METHOD_LABELS,
    ML_REVIEW_PENALTY_LABELS,
    ML_REVIEW_REASON_LABELS,
    ML_REVIEW_SELLER_STATUS_LABELS,
    ML_REVIEW_STAGE_LABELS,
    ML_SELLER_REVIEW_REASON_LABELS,
    ML_SHIPPING_STATUS_LABELS,
    ML_STATUS_MONEY_LABELS,
    MlLabel,
    MP_MONEY_RELEASE_LABELS,
} from '../constants/mlClaimCatalog';
import {
    FlattenReturnInvoice,
    FULL_NO_RECEIPT_MESSAGE,
    HasMlReturnInvoice,
    INSPECTION_DESTINATIONS,
    INSPECTION_DESTINATIONS_TEXT,
    INSPECTION_NOT_RECEIVED_CODE,
    INSPECTION_NOT_RECEIVED_MESSAGE,
    InspectionDestinationMeta,
    IsInspectable,
    IsReturnSkipped,
    IsReturnToUs,
    NeedsCaseReconcile,
    NeedsLossNf,
    NeedsReturnNfe,
    NOT_RECEIVED_STATUSES,
} from '../constants/pedidoTags';
import {
    ChecklistFromRma,
    ChecklistProblemCount,
    DESTINATION_HINTS,
    DESTINATION_RESOLUTIONS,
    GroupInspectionPhotos,
    GUIDANCE_ACTION_LABELS,
    INSPECTION_STEP_KEYS,
    InspectionSuggestion,
    ReturnReasonGroupLabel,
} from '../constants/rmaInspection';
import { useAuth } from '../hooks/useAuth';
import api, { GetToken } from '../services/api';
import { ResolveAssetUrl, ResolveUploadsBaseUrl } from '../utils/assetUrl';
import { SortChronological } from '../utils/chatOrder';
import { CountLabel } from '../utils/format';
import { RmaAssociatedLabels, RmaDestinationLabel } from '../utils/rmaLabels';

// DANFE abre em nova aba — token vai por query (padrão dos anexos).
function DanfeUrl(rmaId) {
    const token = GetToken();
    return `${ResolveUploadsBaseUrl()}/rmas/${rmaId}/invoice/danfe${token ? `?access_token=${token}` : ''}`;
}

// Foto/anexo que o ML colocou na conversa (evidência da revisão) — proxy do
// backend com token por query, pra <img> e nova aba funcionarem.
function ClaimAttachmentUrl(rmaId, filename) {
    const token = GetToken();
    return `${ResolveUploadsBaseUrl()}/rmas/${rmaId}/claim-attachments/${encodeURIComponent(filename)}${token ? `?access_token=${token}` : ''}`;
}

/**
 * Tela do RMA (redesign 2026-08-08) — organizada pela pergunta do operador
 * "o que eu faço agora?", não por departamento:
 *
 *   1. Cabeçalho: identidade + estado + stepper + fatos.
 *   2. PRÓXIMA AÇÃO: uma barra com o passo atual do processo (1 ação primária).
 *   3. Esquerda: a conversa da mediação e as notas/histórico.
 *   4. Direita: painéis quietos de consulta — devolução física, inspeção,
 *      desfecho/estoque, reembolso — cada um com suas ações secundárias.
 */

// Executar o destino (supervisor) = registrar o desfecho do caso: o que
// cada destino faz ao confirmar (texto do modal "Destinar").
const DESTINATION_RESOLVE_INTRO = {
    ESTOQUE: 'Destino definido: Estoque. Registrar o desfecho encerra o caso e enfileira a entrada no estoque (Bling).',
    RMA: 'Destino definido: RMA. Registrar o desfecho encerra o caso e o coloca como candidato ao lote de RMAs ao fabricante.',
    DESCARTE:
        'Destino definido: Descarte. Registrar o desfecho encerra o caso; a NF de perda fica pendente no Financeiro.',
};

// Conclusão do trilho do fabricante: qual foi o retorno do fornecedor.
const SupplierConcludeModal = ({ isOpen, onClose, onConfirm, saving }) => {
    const [outcome, setOutcome] = useState('CREDITO');
    const [note, setNote] = useState('');
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Concluir RMA com o fabricante" size="sm">
            <div className="flex flex-col gap-3">
                <Select label="Retorno do fornecedor" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                    {Object.entries(SUPPLIER_OUTCOME_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </Select>
                <Textarea
                    label="Observações (opcional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} onClick={() => onConfirm({ outcome, note })}>
                        Concluir
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// Edição administrativa (supervisor): corrige status e detalhes com auditoria.
const AdminEditModal = ({ isOpen, onClose, onConfirm, saving, rma }) => {
    const [form, setForm] = useState({
        status: rma.status,
        resolution: rma.resolution || '',
        item_title: rma.item_title || '',
        sku: rma.sku || '',
        quantity: rma.quantity || 1,
        buyer_nickname: rma.buyer_nickname || '',
        reason_detail: rma.reason_detail || '',
        tracking_number: rma.tracking_number || '',
    });
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Editar ocorrência (supervisor)" size="md">
            <div className="flex flex-col gap-3">
                <p className=" bg-warning-50 px-3 py-2 text-xs text-warning-800 dark:bg-warning-400/10 dark:text-warning-300">
                    Edição fora das regras do fluxo — cada mudança fica registrada no histórico.
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <Select label="Etapa (galpão)" value={form.status} onChange={set('status')}>
                        {Object.entries(STATUS_META).map(([value, meta]) => (
                            <option key={value} value={value}>
                                {meta.label}
                            </option>
                        ))}
                    </Select>
                    <Select label="Desfecho" value={form.resolution} onChange={set('resolution')}>
                        <option value="">— sem desfecho —</option>
                        {Object.entries(RESOLUTION_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </Select>
                </div>
                <Input label="Título do produto" value={form.item_title} onChange={set('item_title')} />
                <div className="grid grid-cols-2 gap-3">
                    <Input label="SKU" value={form.sku} onChange={set('sku')} />
                    <Input label="Quantidade" type="number" min="1" value={form.quantity} onChange={set('quantity')} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Input label="Comprador" value={form.buyer_nickname} onChange={set('buyer_nickname')} />
                    <Input label="Rastreio" value={form.tracking_number} onChange={set('tracking_number')} />
                </div>
                <Input label="Motivo" value={form.reason_detail} onChange={set('reason_detail')} />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} onClick={() => onConfirm(form)}>
                        Salvar
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

/**
 * Seção recolhível do "Agora" (redesign 2026-08-26): fechada = 1 linha com o
 * resumo do trilho (colapsado ≠ escondido); aberta = o cartão completo. Só o
 * trilho da próxima ação nasce aberto. `flat` (embutida na Gestão de
 * Pedidos, 2026-09-23): sem borda nem fundo próprio — a seção é uma linha do
 * painel hospedeiro, separada das vizinhas por divisor.
 */
const TrackSection = ({ open, onToggle, title, summary, children, flat = false }) =>
    open ? (
        <div className={flat ? 'flex flex-col' : 'flex flex-col gap-1'}>
            <button
                type="button"
                onClick={onToggle}
                className={`self-start text-[11px] font-bold uppercase tracking-widest text-mono-400 hover:text-black dark:text-mono-600 dark:hover:text-white ${
                    flat ? 'px-5 pt-3' : 'px-1'
                }`}
            >
                ▾ {title}
            </button>
            {children}
        </div>
    ) : (
        <button
            type="button"
            onClick={onToggle}
            className={
                flat
                    ? 'flex w-full items-baseline gap-2 px-5 py-3 text-left transition-colors hover:bg-mono-100/70 dark:hover:bg-mono-800/40'
                    : 'flex w-full items-baseline gap-2 border border-mono-200 bg-white px-5 py-3 text-left transition-colors hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:hover:border-mono-600'
            }
        >
            <span className="shrink-0 text-mono-400 dark:text-mono-600">▸</span>
            <span className="shrink-0 text-[11px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                {title}
            </span>
            <span className="min-w-0 truncate text-[13px] text-mono-600 dark:text-mono-300">{summary}</span>
        </button>
    );

/**
 * Embutida na Gestão de Pedidos (2026-09-23): as seções ficam empilhadas
 * sobre o fundo do painel hospedeiro, separadas por divisores — os `Panel`
 * dos cartões perdem borda, fundo, sombra e anel pelo seletor descendente
 * (`[&_section]`), sem tocar no componente compartilhado.
 */
const EMBEDDED_STACK_CLASS =
    'flex flex-col divide-y divide-mono-200 dark:divide-mono-800 [&_section]:border-0 [&_section]:bg-transparent [&_section]:shadow-none [&_section]:ring-0 dark:[&_section]:bg-transparent';

/**
 * Props (todas opcionais — a rota /rmas/:id renderiza <RmaDetail /> sem nada):
 *   rmaId    — id da ocorrência quando EMBUTIDA em outra tela (Gestão de
 *              Pedidos, 2026-09-22); sem ele, vem de useParams().
 *   embedded — true = sem botão de voltar, sem "Próximo da caixa" e sem o
 *              cabeçalho de identidade (quem embute já mostra a linha da
 *              ocorrência). Abas, cartões, ações e modais são os mesmos; o
 *              "Editar" do supervisor migra para a barra de abas.
 *   onClose  — no modo embutido, chamado onde a tela própria faria
 *              navigate('/rmas') (ex.: ocorrência não encontrada).
 *   onChanged — chamado DEPOIS de cada ação que muda o caso, já com o caso
 *              recarregado: quem embute recarrega o que mostra dele (a Gestão
 *              de Pedidos refaz o pedido — cabeçalho, linha do caso, tags,
 *              histórico). Sem ele, nada muda na rota própria.
 */
const RmaDetail = ({ rmaId: idProp = null, embedded = false, onClose = null, onChanged = null, packId = null }) => {
    // useParams() sempre (rules-of-hooks); a prop, quando vier, tem precedência.
    const params = useParams();
    const rmaId = idProp ?? params.id;
    const navigate = useNavigate();
    // Fila da Caixa de entrada (redesign 2026-08-26): quando o caso foi aberto
    // a partir dela, carrega a ordem — habilita o "Concluir e próximo".
    // Embutida, o state da rota é da tela hospedeira — a ordem não se aplica.
    const location = useLocation();
    const inboxIds = embedded ? null : location.state?.inboxIds || null;
    const toast = useToast();
    const { checkPermission } = useAuth();
    // Sair da ocorrência: na rota própria volta à lista; embutida, devolve o
    // controle a quem embutiu. Ref para o callback não ficar preso no `load`
    // (memoizado só por rmaId).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;
    const leave = () => {
        if (embedded) {
            onCloseRef.current?.();
            return;
        }
        navigate('/rmas');
    };

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    // Executar o destino definido pelo supervisor reusa o "Resolver": com um
    // destino aqui, o modal só oferece os desfechos daquele destino.
    const [resolveDestination, setResolveDestination] = useState(null);
    const [inspectOpen, setInspectOpen] = useState(false);
    // Inspeção registrada pelo assistente em tela cheia: tela "Inspeção
    // concluída" com o checklist até o operador fechar.
    const [inspectDone, setInspectDone] = useState(null);
    // Bloco "Definir destino" (supervisor): escolha em curso (null = a
    // sugestão) e modo "redefinir" de um destino já gravado.
    const [destinationChoice, setDestinationChoice] = useState(null);
    const [destinationEditing, setDestinationEditing] = useState(false);
    const [note, setNote] = useState('');
    const [messages, setMessages] = useState(null);
    // Acordeões do "Agora": default aberto = trilho da próxima ação; toggles
    // manuais valem só para o caso atual (reset ao trocar de ocorrência).
    const [openTracks, setOpenTracks] = useState({});
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [refundForm, setRefundForm] = useState(null);
    const [supplierConcludeOpen, setSupplierConcludeOpen] = useState(false);
    const [tab, setTab] = useState('geral');
    const [adminEditOpen, setAdminEditOpen] = useState(false);
    // Etiqueta do caso antes de o ML confirmar a chegada: pergunta "A
    // devolução chegou ao galpão?" (a etiqueta ASSOCIADA diz "a mercadoria
    // está no galpão"). `labelConfirmReason` = mensagem do servidor quando ele
    // recusou a etiqueta sem a confirmação (422 RMA26_LABEL_BEFORE_ARRIVAL).
    const [labelConfirmOpen, setLabelConfirmOpen] = useState(false);
    const [labelConfirmReason, setLabelConfirmReason] = useState(null);

    // Guarda de staleness: id da ocorrência ATUAL — resposta em voo de um
    // caso anterior (trocamos via 'Próximo da caixa') é descartada em vez de
    // contaminar a tela do caso seguinte.
    const currentIdRef = useRef(rmaId);
    currentIdRef.current = rmaId;

    const load = useCallback(
        async () => {
            const requestedId = rmaId;
            try {
                const data = await api.getRma(requestedId);
                if (currentIdRef.current !== requestedId) {
                    return;
                }
                setDetail(data);
            } catch (err) {
                if (currentIdRef.current !== requestedId) {
                    return;
                }
                toast.error(err.message || 'Não foi possível carregar a ocorrência.');
                leave();
            } finally {
                if (currentIdRef.current === requestedId) {
                    setLoading(false);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rmaId],
    );

    // Depois de uma AÇÃO no caso: recarrega o caso e avisa quem embute
    // (`onChanged`). A carga inicial não avisa — nada mudou ainda.
    const reloadAfterChange = async () => {
        await load();
        onChangedRef.current?.();
    };

    useEffect(() => {
        setLoading(true);
        setMessages(null);
        setOpenTracks({});
        setLabelConfirmOpen(false);
        setLabelConfirmReason(null);
        setDestinationChoice(null);
        setDestinationEditing(false);
        load();
    }, [load]);

    const rma = detail?.rma;

    useEffect(() => {
        if (!rma || messages !== null || !rma.external_claim_id) {
            return;
        }
        const requestedId = rmaId;
        setMessagesLoading(true);
        // Embutida (Gestão de Pedidos): ordem de CHAT garantida aqui, mais
        // antiga em cima — não depende da ordem em que o ML/backend manda.
        const inOrder = (list) => (embedded ? SortChronological(list, 'date_created') : list);
        api.getRmaMediationMessages(requestedId)
            .then((data) => currentIdRef.current === requestedId && setMessages(inOrder(data.messages || [])))
            .catch(() => currentIdRef.current === requestedId && setMessages([]))
            .finally(() => currentIdRef.current === requestedId && setMessagesLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rma?.id]);

    const transition = async (to, extra = {}) => {
        setBusy(true);
        try {
            await api.updateRmaStatus(rmaId, { to_status: to, ...extra });
            toast.success('Feito.');
            setResolveOpen(false);
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível mover a ocorrência.');
        } finally {
            setBusy(false);
        }
    };

    // Assistente do checklist (spec-inspecao, 2026-09-23) em tela cheia. Erro
    // que não seja "ainda não recebida" volta ao assistente (mensagem na tela
    // e, se o servidor apontar a etapa sem fotos suficientes, volta para ela).
    const confirmInspection = async (inspection) => {
        setBusy(true);
        try {
            await api.registerRmaInspection(rmaId, BuildInspectionFormData(inspection));
            toast.success('Inspeção registrada.');
            setInspectDone(inspection);
            await reloadAfterChange();
        } catch (err) {
            if (err?.code === INSPECTION_NOT_RECEIVED_CODE) {
                // Chegada ao CD (rodada 7): o servidor só inspeciona caso
                // RECEBIDO — fecha o assistente e recarrega o caso.
                setInspectOpen(false);
                toast.error(err.message || INSPECTION_NOT_RECEIVED_MESSAGE);
                await load();
                return;
            }
            throw err;
        } finally {
            setBusy(false);
        }
    };
    const closeInspection = () => {
        setInspectOpen(false);
        setInspectDone(null);
    };

    // Destino da devolução inspecionada (ajuste 2 do dono): o SUPERVISOR
    // define ESTOQUE / RMA / DESCARTE (`POST /rmas/:id/destination`); a
    // execução é o "Resolver" de sempre, restrito aos desfechos do destino.
    const saveDestination = async (destination) => {
        setBusy(true);
        try {
            await api.setRmaDestination(rmaId, destination);
            toast.success(`Destino definido: ${InspectionDestinationMeta(destination)?.label || destination}.`);
            setDestinationEditing(false);
            setDestinationChoice(null);
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível definir o destino.');
        } finally {
            setBusy(false);
        }
    };
    const openResolve = (destination = null) => {
        setResolveDestination(destination);
        setResolveOpen(true);
    };

    // `confirmReceived` = o operador confirmou que a devolução já está no
    // galpão antes de o ML marcar a entrega (`confirm_received` no corpo — só
    // vai quando ele confirma).
    const generateCaseLabels = async (confirmReceived = false) => {
        setBusy(true);
        setLabelConfirmOpen(false);
        setLabelConfirmReason(null);
        try {
            const data = await api.generateRmaLabels({
                count: Math.max(1, Number(rma.quantity) || 1),
                rma_id: rmaId,
                ...(confirmReceived ? { confirm_received: true } : {}),
            });
            PrintRmaLabels(data.labels, {
                rma: data.rma || rma,
                toastError: toast.error,
            });
            if (currentIdRef.current === rmaId) {
                setDetail((previous) => ({
                    ...previous,
                    rma: data.rma || previous.rma,
                    labels: [...(previous.labels || []), ...data.labels],
                }));
            }
            await reloadAfterChange();
        } catch (err) {
            if (err?.code === 'RMA26_LABEL_BEFORE_ARRIVAL' && !confirmReceived) {
                // O servidor ainda não vê a chegada: a mensagem dele vai para a
                // pergunta — o operador confirma ou desiste.
                setLabelConfirmReason(err.message || null);
                setLabelConfirmOpen(true);
            } else {
                toast.error(err.message || 'Não foi possível gerar as etiquetas.');
            }
        } finally {
            setBusy(false);
        }
    };

    const markFiscal = async (type) => {
        setBusy(true);
        try {
            await api.markRmaFiscal(rmaId, type);
            toast.success(type === 'ENTRADA' ? 'NF de entrada marcada.' : 'NF de perda marcada.');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível marcar a NF.');
        } finally {
            setBusy(false);
        }
    };

    const reconcile = async () => {
        setBusy(true);
        try {
            await api.reconcileRmaRefund(rmaId);
            toast.success('Caso marcado como conciliado/verificado.');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível marcar o caso como conciliado/verificado.');
        } finally {
            setBusy(false);
        }
    };

    const markSupplierRegistered = async () => {
        setBusy(true);
        try {
            await api.markRmaSupplierRegistered(rmaId);
            toast.success('Cadastro no fabricante registrado.');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível registrar o cadastro.');
        } finally {
            setBusy(false);
        }
    };

    const markSupplierShipped = async () => {
        setBusy(true);
        try {
            await api.markRmaSupplierShipped(rmaId);
            toast.success('Despacho ao fabricante registrado.');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível registrar o despacho.');
        } finally {
            setBusy(false);
        }
    };

    const concludeSupplier = async ({ outcome, note: supplierNote }) => {
        setBusy(true);
        try {
            await api.markRmaSupplierConcluded(rmaId, outcome, supplierNote);
            toast.success('RMA com o fabricante concluído.');
            setSupplierConcludeOpen(false);
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível concluir.');
        } finally {
            setBusy(false);
        }
    };

    const adminSave = async (form) => {
        setBusy(true);
        try {
            await api.adminUpdateRma(rmaId, {
                status: form.status,
                resolution: form.resolution === '' ? null : form.resolution,
                item_title: form.item_title,
                sku: form.sku,
                quantity: Number(form.quantity) || 1,
                buyer_nickname: form.buyer_nickname,
                reason_detail: form.reason_detail,
                tracking_number: form.tracking_number === '' ? null : form.tracking_number,
            });
            toast.success('Ocorrência atualizada.');
            setAdminEditOpen(false);
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível salvar.');
        } finally {
            setBusy(false);
        }
    };

    const sendNote = async () => {
        if (busy || !note.trim()) {
            return;
        }
        setBusy(true);
        try {
            await api.addRmaNote(rmaId, note.trim());
            setNote('');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível salvar a nota.');
        } finally {
            setBusy(false);
        }
    };

    const uploadFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) {
            return;
        }
        setBusy(true);
        try {
            // O backend aceita no máximo 10 arquivos por request — envia em lotes.
            for (let i = 0; i < files.length; i += 10) {
                const formData = new FormData();
                for (const file of files.slice(i, i + 10)) {
                    formData.append('files', file);
                }
                await api.uploadRmaAttachments(rmaId, formData);
            }
            toast.success('Anexo(s) enviado(s).');
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Falha no upload.');
        } finally {
            setBusy(false);
        }
    };

    const saveRefund = async () => {
        setBusy(true);
        try {
            await api.updateRmaRefund(rmaId, {
                refund_status: refundForm.status,
                refund_amount: refundForm.amount === '' ? null : Number(refundForm.amount),
                refund_channel: refundForm.channel || null,
            });
            toast.success('Reembolso atualizado.');
            setRefundForm(null);
            await reloadAfterChange();
        } catch (err) {
            toast.error(err.message || 'Não foi possível atualizar o reembolso.');
        } finally {
            setBusy(false);
        }
    };

    if (loading || !rma) {
        return (
            <div className="flex justify-center py-20">
                <LoadingSpinner />
            </div>
        );
    }

    const canLogistics = checkPermission('rmas:logistics:update');
    const canResolve = checkPermission('rmas:resolve');
    const canFinance = checkPermission('rmas:finance:update');
    const canMediation = checkPermission('rmas:mediation:update');
    // Fluxograma 2026-08-08: inspeção e NF de entrada são do ATENDIMENTO;
    // a logística mantém acesso por operar o galpão.
    const canInspect = canMediation || canLogistics;
    const canSupplier = canMediation || canResolve;
    const canWrite = canLogistics || canResolve || canFinance || canMediation || checkPermission('rmas:create');
    // Tarefa que CRUZA os trilhos: o ML pede o veredito do produto devolvido —
    // depende da inspeção física.
    const needsMlReview = (rma.ml_pending_actions || []).some((a) => a.startsWith('return_review'));

    // Processo de devolução (Thiago, 2026-09-23): a LOGÍSTICA recebe e cola a
    // etiqueta; o ATENDIMENTO inspeciona toda devolução etiquetada. Etiqueta
    // = rma_labels ASSOCIADA a este caso: o backend manda `has_label`; sem
    // ele (backend antigo), deduz das etiquetas do próprio detalhe. Vale na
    // rota /rmas/:id e embutida — a lista /rmas lê o mesmo `has_label`.
    const hasLabel =
        typeof rma.has_label === 'boolean'
            ? rma.has_label
            : (detail?.labels || []).some(
                  (label) => label.status === 'ASSOCIADA' && String(label.rma_id) === String(rma.id),
              );
    const trackRma = { ...rma, has_label: hasLabel };
    // NFs do caso (rodada 7): os componentes fiscais leem a NF-e de devolução
    // do ML nos campos planos `ml_return_invoice_*` — o DTO do caso a traz
    // aninhada (`ml_return_invoice`).
    const fiscalRma = FlattenReturnInvoice(rma);
    const arrivedAtWarehouse = rma.status === 'RECEBIDO' || rma.return_shipment_status === 'delivered';
    // A etiqueta de um caso ABERTO/EM_TRANSITO (não-Full) marca o
    // recebimento: sem a entrega do ML, pergunta antes "A devolução chegou
    // ao galpão?" — mesma regra do servidor (RmaService.generateLabels, 422
    // RMA26_LABEL_BEFORE_ARRIVAL). Nos demais estados, imprime direto.
    // Venda Full não imprime daqui (o recebimento no CD ainda não vale para
    // Full — ver `fullArrivedAction`).
    const labelAllowed = !['ABERTO', 'EM_TRANSITO'].includes(rma.status) || rma.return_shipment_status === 'delivered';
    const associatedLabels = RmaAssociatedLabels(detail.labels, rma);
    const destinationLabel = RmaDestinationLabel(rma);
    const printCaseLabels = (labels = associatedLabels, kind = 'receipt') =>
        PrintRmaLabels(labels, { rma, kind, toastError: toast.error });
    const requestLabels = () => {
        if (associatedLabels.length) {
            printCaseLabels();
            return;
        }
        if (labelAllowed) {
            generateCaseLabels(false);
            return;
        }
        setLabelConfirmReason(null);
        setLabelConfirmOpen(true);
    };

    // Os QUATRO processos do pós-venda, cada um com estado próprio.
    const tracks = ComputeTracks(trackRma);
    // Caso ENCERRADO com prazo vencido: "encerrado", neutro — nunca
    // "estourado há Xd" em vermelho.
    const due = CaseDueInfo(rma);
    const attachments = (detail?.events || []).filter((e) => e.attachment_key);
    // Fotos da inspeção por ETAPA (nota do evento ANEXO) + anexos avulsos.
    const photoGroups = GroupInspectionPhotos(attachments);
    const photoCounts = Object.fromEntries(INSPECTION_STEP_KEYS.map((key) => [key, photoGroups[key].length]));
    const isTerminal = rma.status === 'RESOLVIDO' || rma.status === 'CANCELADO';
    // Recontato: a vez é NOSSA só pela regra da fila recontatos_responder
    // (`RecontactOurTurn`: respondent + claim aberto + prazo por vencer); os
    // demais estão em análise pelo ML. A resposta é no painel do ML.
    const isRecontact = rma.mediation_status === 'RECONTATO';
    const recontactOurs = isRecontact && RecontactOurTurn(rma);
    // Bola conosco: ação OBRIGATÓRIA ou VEREDITO de devolução pendente (este
    // vem sem mandatory, mas com prazo e consequência); no recontato, a regra
    // acima.
    const pendingOurs = (isRecontact ? recontactOurs : Boolean(rma.ml_mandatory_action)) || needsMlReview;
    // Resposta do caso no painel do ML (mensageria da mediação).
    const mlCaseReplyUrl =
        rma.external_order_id && rma.external_claim_id
            ? MlClaimMessagesUrl(rma.external_order_id, rma.external_claim_id, rma.pack_id || packId)
            : MlClaimUrl(rma, packId);
    // "Aguardando": recontato com o vendedor como responsável mas FORA da
    // regra (prazo vencido) está em análise pelo ML — não "Nossa equipe".
    const waitingOn =
        isRecontact && !recontactOurs && ['respondent', 'seller'].includes(rma.mediation_responsible)
            ? 'Mercado Livre (análise)'
            : RESPONSIBLE_LABELS[rma.mediation_responsible];
    const mandatoryLabel = rma.ml_mandatory_action ? ML_ACTION_LABELS[rma.ml_mandatory_action] : null;
    // Conciliar/verificar (rodada 5, decisão do Thiago): TODO caso encerrado —
    // claim fechado no ML ou desfecho local — ainda não conciliado, com ou sem
    // reembolso, e o reembolsado ainda em aberto, como antes
    // (`NeedsCaseReconcile`; a tag `caso_para_conciliar` só conta os
    // encerrados, e a data de corte só filtra a tag).
    const refundedCase = ['REEMBOLSADO', 'PARCIAL'].includes(rma.refund_status);
    const needsReconcile = NeedsCaseReconcile(rma);
    // Caso sem fluxo físico, encerrado no ML e sem desfecho local registrado.
    const outcomePending = tracks.fisico.na && !isTerminal && (tracks.ml.done || tracks.ml.na);
    // Checklist da inspeção (0041); null na inspeção antiga (só issue/damage).
    const checklist = ChecklistFromRma(rma);
    const hasInspection = Boolean(checklist) || (rma.inspection_issue !== null && rma.inspection_issue !== undefined);
    const checklistProblems = checklist ? ChecklistProblemCount(checklist) : 0;
    let inspectionSummary = rma.inspection_damage ? 'com avaria' : 'sem avaria';
    if (checklist) {
        inspectionSummary = checklistProblems ? `checklist com ${checklistProblems} ⚠` : 'checklist ✓ sem problemas';
    }
    // Destino (ajuste 2 do dono, 2026-09-23): a inspeção termina no checklist;
    // o SUPERVISOR (rmas:resolve) define ESTOQUE / RMA / DESCARTE no caso, com
    // a orientação checklist × motivo como sugestão; depois alguém executa
    // (o "Resolver" de sempre, restrito aos desfechos do destino).
    const destination = rma.inspection_destination || null;
    const destinationMeta = InspectionDestinationMeta(destination);
    const destinoVisible = canResolve && rma.status === 'INSPECIONADO';
    const suggestion = destinoVisible ? InspectionSuggestion(rma) : null;
    const chosenDestination = destinationChoice ?? suggestion?.destination ?? null;
    const focusDestination = () => {
        if (!embedded) {
            setTab('geral');
            setOpenTracks((prev) => ({ ...prev, fisico: true }));
        }
        setTimeout(
            () =>
                document
                    .getElementById(`rma-destino-${rma.id}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
            80,
        );
    };
    const supplierApplies = !tracks.fabricante.na;
    // Embutida na Gestão de Pedidos (rodada 2, decisão 4): só "Agora" e, quando
    // o caso tem devolução, a aba "Devolução" (ex-Inspeção). Venda, Garantia e
    // Financeiro do caso saem (o pedido já mostra); as notas próprias também
    // (o Histórico do pedido mescla os `rma_events`).
    const hasReturn = Boolean(rma.return_id) || !tracks.fisico.na;
    // Embutida: sem abas — o workspace e, quando há devolução, a seção
    // "Devolução" ficam empilhados na mesma tela do caso.
    const visibleTabs = [
        ['geral', 'Agora'],
        ['venda', 'Venda'],
        ['inspecao', 'Inspeção'],
        ['garantia', 'Garantia'],
        ['financeiro', 'Financeiro'],
    ];

    // ── Próxima ação unificada: o passo mais urgente entre os trilhos ───────
    // Processo de devolução: chegou ao galpão SEM etiqueta = Logística
    // (etiquetar); COM etiqueta e não inspecionada = Atendimento (inspecionar).
    // Chegada ao CD (rodada 7): inspeção SÓ com o caso RECEBIDO (etiquetado
    // = recebido pela Logística) — antes disso, sem botão e com o aviso.
    const inspectable = IsInspectable(rma);
    const notReceived = !tracks.fisico.na && NOT_RECEIVED_STATUSES.includes(rma.status);
    // Retirada (rodada 7): a devolução está parada numa agência (Correios/ML)
    // esperando a NOSSA retirada — método, rastreio e "na agência desde".
    // Mesma regra da tag devolucao_para_retirar (`IsAwaitingWithdrawal`),
    // sem filtrar pelo status do caso: CANCELADO parado na agência também.
    const withdrawal = !tracks.fisico.na && IsAwaitingWithdrawal(trackRma) ? WithdrawalCase([rma]) : null;
    const withdrawalText = withdrawal ? WithdrawalTitleParts(withdrawal).join(' · ') : '';
    const inspectButton =
        canInspect && inspectable ? { label: 'Inspecionar produto', onClick: () => setInspectOpen(true) } : null;
    // Fluxo físico (etiqueta = recebimento, inspeção, destino) vale para todo
    // caso cuja devolução vem ao NOSSO endereço — Full inclusive (dono, 24/09).
    const physicalFlow = HasPhysicalFlow(rma);
    const printLabelButton =
        canLogistics && physicalFlow ? { label: 'Imprimir etiqueta', onClick: requestLabels } : null;
    const arrivalLabelButton =
        canLogistics && physicalFlow ? { label: 'Chegou? Imprimir etiqueta', onClick: requestLabels } : null;
    const fullArrived = Boolean(!physicalFlow && (hasLabel || arrivedAtWarehouse));
    const fullArrivedAction = {
        text: FULL_NO_RECEIPT_MESSAGE,
        hint: 'A devolução chegou ao nosso endereço, mas etiqueta (recebimento) e inspeção ainda não valem para venda Full — registre o desfecho do caso.',
        track: 'fisico',
        button: canResolve ? { label: 'Resolver', onClick: () => openResolve() } : null,
    };
    const labelAction = {
        text: 'Etiquetar (Logística)',
        hint: 'A devolução chegou ao galpão e está sem etiqueta — imprima e cole na mercadoria; depois o Atendimento inspeciona.',
        track: 'fisico',
        button: printLabelButton,
    };
    const labeledInspectAction = inspectable
        ? {
              text: 'Inspecionar (Atendimento)',
              hint: 'A Logística já colou a etiqueta — bipe na estação ou inspecione aqui.',
              urgent: true,
              track: 'fisico',
              button: inspectButton,
          }
        : {
              text: 'Inspecionar (Atendimento) — aguardando o recebimento',
              hint: INSPECTION_NOT_RECEIVED_MESSAGE,
              track: 'fisico',
              button: null,
          };
    let nextAction = null;
    if (needsMlReview && !tracks.fisico.na && ['ABERTO', 'EM_TRANSITO', 'RECEBIDO'].includes(rma.status)) {
        let reviewText;
        let reviewButton;
        if (fullArrived) {
            reviewText =
                'Devolução para revisar — venda Full (recebimento no CD ainda não suportado): dar o veredito no painel do ML';
            reviewButton = { label: 'Abrir painel ML', href: MlClaimUrl(rma, packId) };
        } else if (hasLabel) {
            reviewText = 'Devolução para revisar — inspecionar (Atendimento) e dar o veredito ao ML';
            reviewButton = inspectButton;
        } else if (arrivedAtWarehouse) {
            reviewText =
                'Devolução para revisar — etiquetar (Logística); depois o Atendimento inspeciona e dá o veredito ao ML';
            reviewButton = printLabelButton;
        } else if (withdrawal) {
            reviewText = !physicalFlow
                ? 'Devolução para revisar — está na agência: a Logística retira; sem fluxo físico aqui: o veredito é no painel do ML'
                : 'Devolução para revisar — está na agência: a Logística retira e etiqueta; depois o Atendimento inspeciona e dá o veredito ao ML';
            reviewButton = null;
        } else {
            // Em trânsito: ainda não é pendência (dono, 2026-09-23 — a tag
            // devolucao_para_revisar exige a chegada).
            reviewText = !physicalFlow
                ? 'Aguardando a mercadoria chegar — sem fluxo físico aqui: depois, dar o veredito no painel do ML'
                : 'Aguardando a mercadoria chegar — depois: etiquetar, inspecionar e dar o veredito ao ML';
            reviewButton = null;
        }
        nextAction = {
            text: reviewText,
            hint:
                rma.ml_status_description ||
                'Sem o veredito no prazo, o ML entende que o produto chegou conforme o esperado.',
            // Urgente só depois da chegada (etiquetada/entregue) ou com a
            // retirada na agência por fazer (ação real da Logística).
            urgent: Boolean(hasLabel || arrivedAtWarehouse || withdrawal),
            track: 'fisico',
            button: reviewButton,
        };
    } else if (needsMlReview) {
        // Sem fluxo físico aqui, ou inspeção já feita: o veredito é no painel.
        nextAction = {
            text: 'Enviar o veredito da revisão no painel do ML',
            hint: hasInspection
                ? `Inspeção feita (${inspectionSummary}) — falta registrar o resultado no Mercado Livre.`
                : rma.ml_status_description ||
                  'Sem o veredito no prazo, o ML entende que o produto chegou conforme o esperado.',
            urgent: true,
            track: 'ml',
            button: { label: 'Abrir painel ML', href: MlClaimUrl(rma, packId) },
        };
    } else if ((!tracks.fisico.na && !tracks.fisico.done) || rma.status === 'INSPECIONADO') {
        // INSPECIONADO entra mesmo sem trilho físico ativo (ex.: venda Full
        // inspecionada antes da regra de chegada): o destino ainda é do
        // supervisor.
        if (rma.status === 'INSPECIONADO' && !destination) {
            // Inspecionado sem destino: a vez é do SUPERVISOR (tag
            // devolucao_para_definir_destino, só para quem tem rmas:resolve).
            nextAction = {
                text: canResolve
                    ? 'Definir o destino da devolução (supervisor)'
                    : 'Aguardando o supervisor definir o destino',
                hint: canResolve
                    ? `Inspeção concluída — ${INSPECTION_DESTINATIONS_TEXT}; a orientação do checklist × motivo vem como sugestão.`
                    : `Inspeção concluída — o supervisor define para onde a mercadoria vai (${INSPECTION_DESTINATIONS_TEXT}).`,
                track: 'fisico',
                button: canResolve ? { label: 'Definir destino', onClick: focusDestination } : null,
            };
        } else if (rma.status === 'INSPECIONADO') {
            // Destino definido, falta EXECUTAR (tag devolucao_para_destinar):
            // o "Resolver" de sempre, só com os desfechos do destino.
            nextAction = {
                text: `Destinar: ${destinationMeta.label}`,
                hint: 'Destino definido pelo supervisor — execute e registre o desfecho (entrada de estoque, lote de RMAs ou descarte).',
                track: 'fisico',
                button: canResolve
                    ? { label: `Destinar: ${destinationMeta.label}`, onClick: () => openResolve(destination) }
                    : null,
            };
        } else if (fullArrived) {
            nextAction = fullArrivedAction;
        } else if (hasLabel) {
            nextAction = labeledInspectAction;
        } else if (arrivedAtWarehouse) {
            nextAction = labelAction;
        } else if (withdrawal) {
            let afterWithdrawal = 'Depois de retirada: a Logística etiqueta; o Atendimento inspeciona.';
            if (!physicalFlow) {
                afterWithdrawal = `${FULL_NO_RECEIPT_MESSAGE} (sem etiqueta nem inspeção aqui).`;
            } else if (isTerminal) {
                afterWithdrawal = 'Caso encerrado — depois de retirada, a Logística etiqueta a mercadoria.';
            }
            nextAction = {
                text: 'Retirar a devolução na agência (Logística)',
                hint: `${withdrawalText ? `${withdrawalText}. ` : ''}${afterWithdrawal}`,
                track: 'fisico',
                button: arrivalLabelButton,
            };
        } else {
            // Ainda não entregue pelo ML: se a devolução já está aqui, a
            // etiqueta pergunta "A devolução chegou ao galpão?" antes de sair.
            nextAction = {
                text: 'Aguardando a mercadoria chegar',
                hint: !physicalFlow
                    ? `${FULL_NO_RECEIPT_MESSAGE} — quando chegar, registre o desfecho do caso.`
                    : 'Quando chegar: a Logística imprime e cola a etiqueta; depois o Atendimento inspeciona.',
                track: 'fisico',
                button: arrivalLabelButton,
            };
        }
    } else if (supplierApplies && !tracks.fabricante.done) {
        if (!rma.supplier_registered_at) {
            nextAction = {
                text: 'Cadastrar o RMA no fabricante',
                hint: 'Daqui em diante não há mais pendência com o Mercado Livre — o processo é com o fornecedor.',
                track: 'fabricante',
                button: canSupplier ? { label: 'Marcar cadastrado', onClick: markSupplierRegistered } : null,
            };
        } else if (!rma.supplier_shipped_at) {
            nextAction = {
                text: 'Despachar a mercadoria ao fabricante',
                hint: 'Cadastro feito — falta enviar o produto ao fornecedor.',
                track: 'fabricante',
                button: canSupplier ? { label: 'Marcar despachado', onClick: markSupplierShipped } : null,
            };
        } else {
            nextAction = {
                text: 'Aguardando o retorno do fabricante',
                hint: 'Quando o fornecedor responder: registrar crédito, reposição ou descarte.',
                track: 'fabricante',
                button: canSupplier ? { label: 'Concluir', onClick: () => setSupplierConcludeOpen(true) } : null,
            };
        }
    } else if (needsReconcile && (refundedCase || !outcomePending)) {
        // Sem reembolso e com o desfecho local por registrar, o desfecho vem
        // antes (ramo abaixo); a conciliação aparece em seguida. Embutida: o
        // valor mora na aba Financeiro do pedido (payments:read) — aqui só a
        // ação, com rmas:finance:update.
        let reconcileHint;
        if (embedded) {
            reconcileHint = 'Também na aba "Financeiro" do pedido, bloco "Conciliação dos casos".';
        } else if (refundedCase) {
            reconcileHint = `${REFUND_LABELS[rma.refund_status]}${
                rma.refund_amount !== null
                    ? ` · R$ ${Number(rma.refund_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                    : ''
            }`;
        } else {
            reconcileHint = 'Caso encerrado — conferir o efeito no dinheiro da venda e marcar como verificado.';
        }
        nextAction = {
            text: refundedCase
                ? 'Financeiro: conferir se o reembolso bateu'
                : 'Financeiro: conciliar/verificar o caso encerrado',
            hint: reconcileHint,
            button: canFinance ? { label: 'Marcar conciliado/verificado', onClick: reconcile } : null,
            finance: true,
            track: 'financeiro',
        };
    } else if (outcomePending) {
        nextAction = {
            text: 'Caso encerrado no ML — registrar o desfecho',
            hint: !physicalFlow
                ? 'Full: o produto ficou com o armazém do ML.'
                : 'Sem fluxo físico aqui — resolver fecha o registro.',
            track: 'fisico',
            button: canResolve ? { label: 'Resolver', onClick: () => openResolve() } : null,
        };
    } else if (tracks.allDone) {
        nextAction = {
            text: 'Tudo concluído',
            hint: 'Os quatro processos estão encerrados — nada pendente nesta ocorrência.',
            doneAll: true,
        };
    }

    // Bola conosco no ML sem fluxo de revisão: vira A próxima ação — uma
    // barra só (o banner vermelho separado morreu no redesign 2026-08-26).
    if (recontactOurs && !needsMlReview) {
        // Recontato com a vez nossa: a API não oferece ação — responde-se na
        // mensageria da mediação no painel do ML.
        nextAction = {
            text: 'Responder o recontato no ML',
            hint: `${due && !due.closed ? `Prazo do ML: ${due.label}. ` : ''}A vez é da nossa equipe — a resposta é dada no painel do ML (a API não oferece ação).`,
            urgent: true,
            track: 'ml',
            button: { label: 'Responder no ML', href: mlCaseReplyUrl },
        };
    } else if (pendingOurs && !tracks.ml.done && !needsMlReview) {
        nextAction = {
            text: mandatoryLabel
                ? `Ação obrigatória: ${mandatoryLabel}`
                : `Responder a ${rma.mediation_status === 'EM_MEDIACAO' ? 'mediação' : 'reclamação'}`,
            hint: 'A vez é da nossa equipe — a conversa está no cartão do ML; a resposta é pelo painel do ML.',
            urgent: true,
            track: 'ml',
            button: rma.source === 'ML' ? { label: 'Responder no Mercado Livre', href: MlClaimUrl(rma, packId) } : null,
        };
    } else if (!nextAction && isRecontact && !tracks.ml.done && !tracks.ml.na) {
        // Recontato fora da nossa vez e nada mais pendente: acompanhar.
        nextAction = {
            text: 'Recontato em análise pelo ML',
            hint: 'Nada a responder agora — acompanhe. Se o ML devolver a vez para nós, a próxima ação vira "Responder o recontato no ML".',
            track: 'ml',
            button: { label: 'Acompanhar no ML', href: mlCaseReplyUrl },
        };
    }
    const nextInboxId = (() => {
        if (!inboxIds?.length) {
            return null;
        }
        const idx = inboxIds.indexOf(rmaId);
        return idx >= 0 && idx + 1 < inboxIds.length ? inboxIds[idx + 1] : null;
    })();

    // Hierarquia: o cartão do processo que exige ação AGORA domina — anel de
    // destaque e primeiro da pilha; os demais ficam quietos.
    const activeTrack = nextAction?.track || (pendingOurs ? 'ml' : null);
    const ringOf = (key) => (activeTrack === key ? 'ring-2 ring-black dark:ring-white' : '');
    // Default dinâmico: seção sem toggle manual segue o trilho da próxima ação.
    const isOpenTrack = (key) => openTracks[key] ?? key === activeTrack;
    const toggleTrack = (key) => setOpenTracks((prev) => ({ ...prev, [key]: !(prev[key] ?? key === activeTrack) }));

    // Cartões de processo — reutilizados pelo Geral e pelas abas de mergulho.
    const mlCard = (
        <div key="ml">
            {!tracks.ml.na && (
                <Panel
                    className={ringOf('ml')}
                    title="Mercado Livre — reclamação e mediação"
                    action={
                        <span className="flex items-center gap-2 text-[11px] font-semibold text-mono-500">
                            {rma.source === 'ML' && (
                                <a
                                    href={MlClaimUrl(rma, packId)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                >
                                    abrir no ML <ArrowSquareOut size={12} />
                                </a>
                            )}
                            {tracks.ml.label}
                        </span>
                    }
                >
                    {rma.ml_status_title && (
                        <div className=" bg-mono-50 px-3 py-2 dark:bg-mono-900/60">
                            <p className="text-sm font-semibold text-black dark:text-white">{rma.ml_status_title}</p>
                            {rma.ml_status_description && (
                                <p className="text-xs text-mono-500 dark:text-mono-400">{rma.ml_status_description}</p>
                            )}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                        <Fact label="Tipo">
                            {rma.claim_type ? MlLabel(ML_CLAIM_TYPE_LABELS, rma.claim_type) : null}
                        </Fact>
                        <Fact label="Fase">
                            {rma.mediation_status === 'EM_MEDIACAO'
                                ? 'Mediação'
                                : rma.claim_stage
                                  ? MlLabel(ML_CLAIM_STAGE_LABELS, rma.claim_stage)
                                  : null}
                        </Fact>
                        <Fact label="Aguardando">{waitingOn}</Fact>
                        <Fact label="Prazo">{due ? due.label : null}</Fact>
                        <Fact label="Problema">{rma.ml_problem}</Fact>
                        <Fact label="Decisão ML">
                            {rma.ml_resolution_reason
                                ? `${MlLabel(ML_RESOLUTION_REASON_LABELS, rma.ml_resolution_reason)}${
                                      rma.ml_closed_by
                                          ? ` · encerrado por ${MlLabel(ML_CLOSED_BY_LABELS, rma.ml_closed_by)}`
                                          : ''
                                  }`
                                : null}
                        </Fact>
                        <Fact label="Beneficiado">
                            {rma.ml_benefited
                                ? [
                                      ...new Set(
                                          rma.ml_benefited
                                              .split(',')
                                              .map((side) => MlLabel(ML_BENEFITED_LABELS, side.trim())),
                                      ),
                                  ].join(' + ')
                                : null}
                        </Fact>
                    </div>
                    {(rma.ml_pending_actions || []).length > 0 && (
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            <span className="font-semibold">O ML nos oferece:</span>{' '}
                            {[
                                ...new Set(
                                    (rma.ml_pending_actions || []).map((action) =>
                                        MlLabel(ML_ACTION_LABELS, action, action),
                                    ),
                                ),
                            ].join(' · ')}
                            {rma.ml_mandatory_action
                                ? ` — obrigatória: ${MlLabel(ML_ACTION_LABELS, rma.ml_mandatory_action)}`
                                : ''}
                        </p>
                    )}
                    <MediationChat
                        messages={messages}
                        loading={messagesLoading}
                        attachmentUrl={(filename) => ClaimAttachmentUrl(rma.id, filename)}
                    />
                </Panel>
            )}
        </div>
    );
    const notasCard = (
        <div key="notas">
            <Panel title={`Notas e histórico (${(detail.events || []).length})`}>
                {canWrite && (
                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <Input
                                placeholder="Escrever nota interna…"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && sendNote()}
                            />
                        </div>
                        <Button size="sm" variant="secondary" onClick={sendNote} disabled={busy || !note.trim()}>
                            Anotar
                        </Button>
                    </div>
                )}
                <div className="flex max-h-96 flex-col overflow-y-auto">
                    {(detail.events || []).map((e, i) => (
                        <div
                            key={e.id}
                            className={`flex gap-3 py-2.5 ${i > 0 ? 'border-t border-mono-100 dark:border-mono-900' : ''}`}
                        >
                            <div className="w-28 shrink-0 pt-0.5">
                                <p className="text-[11px] font-medium text-mono-500">{fmtDate(e.created_at)}</p>
                                <p className="truncate text-[11px] text-mono-400">{e.author_name || 'sistema'}</p>
                            </div>
                            <div className="min-w-0 flex-1 text-sm">
                                {(e.from_value || e.to_value) && (
                                    <p className="font-medium text-black dark:text-white">
                                        {e.from_value ? `${e.from_value} → ` : ''}
                                        {e.to_value}
                                    </p>
                                )}
                                {e.note && (
                                    <p className="whitespace-pre-wrap text-mono-600 dark:text-mono-300">{e.note}</p>
                                )}
                                {e.attachment_key && (
                                    <a
                                        href={ResolveAssetUrl(e.attachment_key)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs font-medium underline underline-offset-2"
                                    >
                                        📎 {e.attachment_name || 'anexo'}
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
        </div>
    );
    const galpaoCard = (
        <div key="fisico">
            <Panel
                className={ringOf('fisico')}
                title="Galpão — devolução física"
                action={
                    !tracks.fisico.na && canLogistics && physicalFlow ? (
                        <div className="flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={requestLabels}
                                disabled={busy}
                                title={
                                    associatedLabels.length || labelAllowed
                                        ? undefined
                                        : 'O ML ainda não marcou a entrega — antes de imprimir, confirme que a devolução chegou ao galpão.'
                                }
                                className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                            >
                                {associatedLabels.length
                                    ? 'reimprimir etiqueta 1'
                                    : labelAllowed
                                      ? 'imprimir etiqueta 1'
                                      : 'chegou? imprimir etiqueta 1'}
                            </button>
                            {destinationLabel && associatedLabels.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => printCaseLabels(associatedLabels, 'destination')}
                                    disabled={busy}
                                    title="Imprimir ou reimprimir a etiqueta do destino confirmado pelo supervisor."
                                    className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                >
                                    etiqueta 2 · {destinationLabel}
                                </button>
                            )}
                        </div>
                    ) : null
                }
            >
                {tracks.fisico.na ? (
                    <>
                        <p className="text-sm text-mono-500">
                            {rma.is_full
                                ? 'Logística Full — o produto volta ao armazém do ML, sem passar pelo galpão.'
                                : tracks.fisico.label === 'decidido no armazém ML'
                                  ? 'A devolução foi para o armazém do ML e a decisão saiu de lá — a mercadoria não vem para o galpão.'
                                  : 'Este caso não tem devolução física vinculada.'}
                        </p>
                        {/* O ML só emite a NF-e de devolução nas devoluções que vão
                            ao armazém dele (integração 2026-09-23: 389 de 389) —
                            justamente este ramo; na tela antiga ela aparece aqui
                            também (a embutida mostra no bloco "Notas fiscais"). */}
                        {!embedded && HasMlReturnInvoice(fiscalRma) ? (
                            <CaseFiscalDocText occ={fiscalRma} type="ENTRADA" />
                        ) : null}
                    </>
                ) : (
                    <>
                        <PipelineStepper rma={rma} />
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                            <Fact label="Rastreio" mono>
                                {rma.tracking_number}
                            </Fact>
                            <Fact label="Status do envio">
                                {rma.return_shipment_status
                                    ? MlLabel(ML_RETURN_STATUS_LABELS, rma.return_shipment_status)
                                    : null}
                            </Fact>
                            <Fact label="Recebido">{fmtDate(rma.received_at)}</Fact>
                            {/* Embutida (rodada 7): a NF-e de devolução mora no bloco
                                "Notas fiscais" da seção Devolução, com o upload do XML. */}
                            {!embedded && (
                                <Fact label="NF de entrada">
                                    {rma.nf_entry_at ? fmtDate(rma.nf_entry_at) : 'não emitida'}
                                </Fact>
                            )}
                            {/* Número/chave/XML da NF de entrada, ou a NF-e de devolução
                                emitida pelo ML (DANFE) — só exibição na tela antiga. */}
                            {!embedded && (HasMlReturnInvoice(fiscalRma) || rma.nf_entry_number) ? (
                                <div className="col-span-2">
                                    <CaseFiscalDocText occ={fiscalRma} type="ENTRADA" />
                                </div>
                            ) : null}
                        </div>
                        {withdrawal && (
                            <p className="border-l-2 border-l-amber-500 px-2.5 py-1 text-xs text-mono-700 dark:text-mono-200">
                                Parada na agência esperando a nossa retirada
                                {withdrawalText ? ` — ${withdrawalText}` : ''}
                            </p>
                        )}
                        {!physicalFlow && !isTerminal ? (
                            <p className="border-l-2 border-l-mono-400 px-2.5 py-1 text-xs text-mono-600 dark:text-mono-300">
                                {FULL_NO_RECEIPT_MESSAGE} — etiqueta e inspeção não se aplicam a venda Full.
                            </p>
                        ) : (
                            notReceived && (
                                <p className="border-l-2 border-l-amber-500 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
                                    {INSPECTION_NOT_RECEIVED_MESSAGE}.
                                </p>
                            )
                        )}
                        {associatedLabels.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {associatedLabels.map((label) => (
                                    <button
                                        key={label.id}
                                        type="button"
                                        onClick={() => printCaseLabels([label])}
                                        disabled={!canLogistics || busy}
                                        title="Reimprimir etiqueta 1 desta unidade"
                                        className="bg-mono-100 px-2 py-0.5 font-mono text-xs font-semibold enabled:hover:underline dark:bg-mono-900"
                                    >
                                        {label.code}
                                    </button>
                                ))}
                            </div>
                        )}
                        {hasInspection && (
                            <div className="flex flex-col gap-2 border-t border-mono-100 pt-2 dark:border-mono-900">
                                {/* Checklist ✓/⚠ (0041). Com o bloco "Definir destino"
                                    aberto (supervisor), ele mora lá, com as fotos. */}
                                {checklist && !destinoVisible && (
                                    <InspectionChecklist
                                        checklist={checklist}
                                        serialNumber={rma.serial_number || null}
                                        photoCounts={photoCounts}
                                    />
                                )}
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                    {!checklist && (
                                        <>
                                            <Fact label="Conferência">
                                                {rma.inspection_issue ? '⚠ Com problema' : 'OK'}
                                            </Fact>
                                            <Fact label="Nº de série" mono>
                                                {rma.serial_number}
                                            </Fact>
                                            <Fact label="Avaria">{rma.inspection_damage ? '⚠ Sim' : 'Não'}</Fact>
                                        </>
                                    )}
                                    <Fact label="Inspecionado em">{fmtDate(rma.inspected_at)}</Fact>
                                    {(rma.status === 'INSPECIONADO' || destinationMeta) && (
                                        <Fact label="Destino">
                                            {destinationMeta ? destinationMeta.label : 'a definir (supervisor)'}
                                        </Fact>
                                    )}
                                    {rma.resolution && (
                                        <Fact label="Desfecho">
                                            {RESOLUTION_LABELS[rma.resolution]} · {fmtDate(rma.resolved_at)}
                                        </Fact>
                                    )}
                                </div>
                            </div>
                        )}
                        {attachments.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {attachments.map((a) => (
                                    <a
                                        key={a.id}
                                        href={ResolveAssetUrl(a.attachment_key)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className=" bg-mono-100 px-2 py-1 text-xs underline-offset-2 hover:underline dark:bg-mono-900"
                                    >
                                        📎 {a.attachment_name || 'foto'}
                                    </a>
                                ))}
                            </div>
                        )}
                        {(detail.stock_movements || []).map((m) => (
                            <p key={m.id} className="text-sm text-mono-600 dark:text-mono-300">
                                {m.quantity}× <span className="font-mono">{m.sku}</span> —{' '}
                                {MOVEMENT_STATUS_LABELS[m.status] || m.status}
                                {m.bling_stock_id ? ` (Bling #${m.bling_stock_id})` : ''}
                            </p>
                        ))}
                        <div className="flex flex-wrap gap-2">
                            {canInspect && inspectable && (
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setInspectOpen(true)}
                                    disabled={busy}
                                >
                                    Inspecionar
                                </Button>
                            )}
                            {canResolve && rma.status === 'INSPECIONADO' && destinationMeta && (
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => openResolve(destination)}
                                    disabled={busy}
                                >
                                    Destinar: {destinationMeta.label}
                                </Button>
                            )}
                            {canResolve && rma.status !== 'INSPECIONADO' && fullArrived && !isTerminal && (
                                <Button size="sm" variant="secondary" onClick={() => openResolve()} disabled={busy}>
                                    Resolver
                                </Button>
                            )}
                            {canInspect && !isTerminal && (
                                <label className="cursor-pointer self-center text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white">
                                    anexar arquivos
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*,.pdf,video/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            uploadFiles(e.target.files);
                                            e.target.value = '';
                                        }}
                                    />
                                </label>
                            )}
                            {/* Tela antiga: marca sem arquivo. Embutida (Gestão de Pedidos):
                                só com o XML, no bloco "Notas fiscais". */}
                            {!embedded && rma.received_at && !rma.nf_entry_at && (canInspect || canFinance) && (
                                <Button size="sm" variant="ghost" onClick={() => markFiscal('ENTRADA')} disabled={busy}>
                                    Marcar NF de entrada
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </Panel>
        </div>
    );
    // Bloco "Definir destino" (supervisor, rmas:resolve — só em INSPECIONADO):
    // checklist ✓/⚠, fotos por etapa, motivo, a ORIENTAÇÃO como sugestão
    // pré-selecionada e os três destinos. Definido: "Destinar: X" (resolver
    // restrito ao destino) + "redefinir" enquanto o caso não for resolvido.
    const suggestionActions = suggestion?.actions || [];
    const suggestionMlLink = suggestionActions.some((action) => ['aprovar_ml', 'contestar_ml'].includes(action));
    const destinoCard = destinoVisible ? (
        <div
            key="destino"
            id={`rma-destino-${rma.id}`}
            className={embedded && !destination ? 'border-l-4 border-l-amber-500' : ''}
        >
            <Panel
                className={destination ? '' : 'ring-2 ring-black dark:ring-white'}
                title={destination ? `Destino definido — ${destinationMeta.label}` : 'Definir destino'}
                action={
                    <span className="text-[11px] text-mono-400 dark:text-mono-500">
                        {destination && rma.inspection_destination_at
                            ? `definido em ${fmtDate(rma.inspection_destination_at)}`
                            : 'supervisor · rmas:resolve'}
                    </span>
                }
            >
                {checklist ? (
                    <InspectionChecklist
                        checklist={checklist}
                        serialNumber={rma.serial_number || null}
                        photoCounts={photoCounts}
                    />
                ) : (
                    <p className="text-xs text-mono-500 dark:text-mono-400">
                        Inspeção sem checklist (anterior ao checklist de 23/09): conferência{' '}
                        <strong>{rma.inspection_issue ? '⚠ com problema' : 'OK'}</strong> · avaria{' '}
                        <strong>{rma.inspection_damage ? '⚠ sim' : 'não'}</strong>
                        {rma.serial_number ? ` · série ${rma.serial_number}` : ''}
                    </p>
                )}
                <InspectionPhotoGroups groups={photoGroups} thumbClass="h-16 w-16" />
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                    <Fact label="Motivo da devolução">{rma.reason_detail || rma.ml_problem || rma.reason_id}</Fact>
                    <Fact label="Grupo do motivo">{ReturnReasonGroupLabel(suggestion?.reasonGroup)}</Fact>
                </div>
                {suggestion && (
                    <div className="flex flex-col gap-1.5 border-l-4 border-l-black bg-mono-50 px-4 py-3 dark:border-l-white dark:bg-mono-900/60">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                            Orientação (checklist × motivo) — sugestão
                        </p>
                        <p className="text-sm font-semibold text-black dark:text-white">{suggestion.label}</p>
                        {suggestion.destination && (
                            <p className="text-xs text-mono-600 dark:text-mono-300">
                                Destino sugerido:{' '}
                                <strong>{InspectionDestinationMeta(suggestion.destination)?.label}</strong>
                            </p>
                        )}
                        {suggestionActions.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                <span className="text-[11px] text-mono-500 dark:text-mono-400">Pendências:</span>
                                {suggestionActions.map((action) => (
                                    <span
                                        key={action}
                                        className="border border-mono-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-mono-700 dark:border-mono-700 dark:bg-mono-950 dark:text-mono-200"
                                    >
                                        {GUIDANCE_ACTION_LABELS[action] || action}
                                    </span>
                                ))}
                                {suggestionMlLink && (
                                    <a
                                        href={MlClaimUrl(rma, packId)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1 text-[11px] font-semibold text-mono-600 underline underline-offset-2 hover:text-black dark:text-mono-300 dark:hover:text-white"
                                    >
                                        abrir no painel do ML <ArrowSquareOut size={12} />
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {destination && !destinationEditing ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <Button onClick={() => openResolve(destination)} disabled={busy}>
                            Destinar: {destinationMeta.label}
                        </Button>
                        <button
                            type="button"
                            onClick={() => {
                                setDestinationChoice(destination);
                                setDestinationEditing(true);
                            }}
                            disabled={busy}
                            className="text-xs font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                        >
                            redefinir destino
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            {INSPECTION_DESTINATIONS.map((key) => {
                                const selected = chosenDestination === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        aria-pressed={selected}
                                        onClick={() => setDestinationChoice(key)}
                                        className={`flex flex-col items-start gap-1 border-2 px-4 py-3 text-left transition-colors ${
                                            selected
                                                ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                                                : 'border-mono-300 bg-white text-mono-700 hover:border-black dark:border-mono-700 dark:bg-mono-950 dark:text-mono-200 dark:hover:border-white'
                                        }`}
                                    >
                                        <span className="flex w-full items-center justify-between gap-2 text-base font-black uppercase tracking-wide">
                                            {key}
                                            {suggestion?.destination === key && (
                                                <span className="border border-current px-1.5 py-px text-[10px] font-bold tracking-widest">
                                                    sugerido
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[11px] opacity-80">{DESTINATION_HINTS[key]}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            <Button
                                onClick={() => saveDestination(chosenDestination)}
                                loading={busy}
                                disabled={!chosenDestination || chosenDestination === destination}
                            >
                                {chosenDestination
                                    ? `${destination ? 'Redefinir' : 'Definir'} destino: ${
                                          InspectionDestinationMeta(chosenDestination)?.label
                                      }`
                                    : 'Escolha o destino'}
                            </Button>
                            {destinationEditing && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDestinationEditing(false);
                                        setDestinationChoice(null);
                                    }}
                                    className="text-xs font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                >
                                    cancelar
                                </button>
                            )}
                        </div>
                    </>
                )}
            </Panel>
        </div>
    ) : null;
    const fabricanteCard = (
        <div key="fabricante">
            {supplierApplies && (
                <Panel className={ringOf('fabricante')} title="Fabricante — RMA com o fornecedor">
                    <p className="text-xs text-mono-500">
                        Sem pendências com o Mercado Livre daqui em diante — o processo é nosso com o fornecedor.
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <Fact label="Cadastrado">
                            {rma.supplier_registered_at ? fmtDate(rma.supplier_registered_at) : 'pendente'}
                        </Fact>
                        <Fact label="Despachado">
                            {rma.supplier_shipped_at
                                ? fmtDate(rma.supplier_shipped_at)
                                : rma.supplier_registered_at
                                  ? 'pendente'
                                  : null}
                        </Fact>
                        <Fact label="NF de devolução">
                            {rma.supplier_nf_number
                                ? `${rma.supplier_nf_number}${rma.supplier_nf_at ? ` · ${fmtDate(rma.supplier_nf_at)}` : ''}`
                                : rma.supplier_shipped_at
                                  ? 'não registrada'
                                  : null}
                        </Fact>
                        <Fact label="Concluído">
                            {rma.supplier_concluded_at ? fmtDate(rma.supplier_concluded_at) : null}
                        </Fact>
                        <Fact label="Retorno">
                            {rma.supplier_outcome ? SUPPLIER_OUTCOME_LABELS[rma.supplier_outcome] : null}
                        </Fact>
                    </div>
                    {canSupplier && !rma.supplier_concluded_at && (
                        <div className="flex flex-wrap gap-2">
                            {!rma.supplier_registered_at && (
                                <Button size="sm" variant="secondary" onClick={markSupplierRegistered} disabled={busy}>
                                    Marcar cadastrado
                                </Button>
                            )}
                            {rma.supplier_registered_at && !rma.supplier_shipped_at && (
                                <Button size="sm" variant="secondary" onClick={markSupplierShipped} disabled={busy}>
                                    Marcar despachado
                                </Button>
                            )}
                            {rma.supplier_shipped_at && (
                                <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => setSupplierConcludeOpen(true)}
                                    disabled={busy}
                                >
                                    Concluir com retorno
                                </Button>
                            )}
                        </div>
                    )}
                </Panel>
            )}
        </div>
    );
    // Análise da devolução feita pelo PRÓPRIO ML (triagem no armazém e/ou
    // revisão): condição do produto, destino, motivo e nosso veredito — o que
    // o Giovanni via só no painel do ML (pedido 2026-08-19).
    const mlReviewCard =
        (detail.return_reviews || []).length > 0 ? (
            <Panel key="ml-review" title="Análise do ML (revisão da devolução)">
                {(detail.return_reviews || []).map((review, i) => (
                    <div
                        key={i}
                        className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-mono-100 pt-2 first:border-t-0 first:pt-0 dark:border-mono-900 sm:grid-cols-3"
                    >
                        <Fact label="Como foi revisada">{MlLabel(ML_REVIEW_METHOD_LABELS, review.method)}</Fact>
                        <Fact label="Etapa">{MlLabel(ML_REVIEW_STAGE_LABELS, review.stage)}</Fact>
                        <Fact label="Condição do produto">
                            {review.product_condition
                                ? MlLabel(ML_PRODUCT_CONDITION_LABELS, review.product_condition)
                                : null}
                        </Fact>
                        <Fact label="Destino do produto">
                            {review.product_destination
                                ? MlLabel(ML_PRODUCT_DESTINATION_LABELS, review.product_destination)
                                : null}
                        </Fact>
                        <Fact label="Motivo da triagem">
                            {review.reason_id ? MlLabel(ML_REVIEW_REASON_LABELS, review.reason_id) : null}
                        </Fact>
                        <Fact label="Nosso veredito">
                            {review.seller_status
                                ? `${MlLabel(ML_REVIEW_SELLER_STATUS_LABELS, review.seller_status)}${
                                      review.seller_reason
                                          ? ` — ${MlLabel(ML_SELLER_REVIEW_REASON_LABELS, review.seller_reason)}`
                                          : ''
                                  }`
                                : null}
                        </Fact>
                        {(review.benefited || review.benefited_reason) && (
                            <Fact label="Resultado">
                                {[
                                    review.benefited ? MlLabel(ML_BENEFITED_LABELS, review.benefited) : null,
                                    review.benefited_reason
                                        ? MlLabel(ML_REVIEW_PENALTY_LABELS, review.benefited_reason)
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Fact>
                        )}
                        {review.missing_quantity !== null && review.missing_quantity > 0 && (
                            <Fact label="Faltando">{`${review.missing_quantity} un`}</Fact>
                        )}
                        <Fact label="Atualizada em">{fmtDate(review.last_updated || review.date_created)}</Fact>
                    </div>
                ))}
            </Panel>
        ) : null;

    const financeiroCard = (
        <div key="financeiro">
            <Panel
                className={ringOf('financeiro')}
                title="Financeiro — reembolso"
                action={
                    <span className="flex items-center gap-3">
                        {rma.external_order_id && (
                            <button
                                type="button"
                                onClick={() => navigate(`/payments/order/${rma.external_order_id}`)}
                                className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                            >
                                ver em Pagamentos
                            </button>
                        )}
                        {canFinance && !refundForm && (
                            <button
                                type="button"
                                onClick={() =>
                                    setRefundForm({
                                        status: rma.refund_status,
                                        amount: rma.refund_amount ?? '',
                                        channel: rma.refund_channel || '',
                                    })
                                }
                                className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                            >
                                editar
                            </button>
                        )}
                    </span>
                }
            >
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <Fact label="Situação">{REFUND_LABELS[rma.refund_status]}</Fact>
                    <Fact label="Dinheiro da venda">
                        {rma.status_money ? MlLabel(ML_STATUS_MONEY_LABELS, rma.status_money) : null}
                    </Fact>
                    <Fact label="Valor">
                        {rma.refund_amount !== null
                            ? `R$ ${Number(rma.refund_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                            : null}
                    </Fact>
                    <Fact label="Canal">
                        {rma.refund_channel === 'POR_FORA'
                            ? 'Por fora (Pix)'
                            : rma.refund_channel === 'ML'
                              ? 'Pelo ML'
                              : null}
                    </Fact>
                    <Fact label="Conciliação">
                        {rma.refund_reconciled_at
                            ? fmtDate(rma.refund_reconciled_at)
                            : rma.reconciliation_not_applicable === true
                              ? 'dispensada — cancelamento sem movimentação'
                              : 'ainda não'}
                    </Fact>
                    <Fact label="NF de perda">{rma.nf_loss_at ? fmtDate(rma.nf_loss_at) : 'não emitida'}</Fact>
                    {rma.nf_loss_number ? (
                        <div className="col-span-2">
                            <CaseFiscalDocText occ={fiscalRma} type="PERDA" />
                        </div>
                    ) : null}
                </div>
                {(detail.payments || []).length > 0 && (
                    <div className="flex flex-col gap-2 border-t border-mono-100 pt-2 dark:border-mono-900">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                            Pagamento do pedido (Mercado Pago)
                        </p>
                        {(detail.payments || []).map((pay) => (
                            <div key={pay.id} className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                <Fact label="Valor pago">
                                    {pay.transaction_amount !== null
                                        ? `R$ ${pay.transaction_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                        : null}
                                </Fact>
                                <Fact label="Estornado ao comprador">
                                    {pay.transaction_amount_refunded ? (
                                        <span className="font-semibold text-danger-600 dark:text-danger-400">
                                            R${' '}
                                            {pay.transaction_amount_refunded.toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                            })}
                                        </span>
                                    ) : (
                                        'nada'
                                    )}
                                </Fact>
                                <Fact label="Líquido recebido">
                                    {pay.net_received_amount !== null
                                        ? `R$ ${pay.net_received_amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                        : null}
                                </Fact>
                                <Fact label="Liberação">
                                    {pay.money_release_status
                                        ? `${MlLabel(MP_MONEY_RELEASE_LABELS, pay.money_release_status)}${pay.money_release_date ? ` · ${fmtDate(pay.money_release_date)}` : ''}`
                                        : null}
                                </Fact>
                            </div>
                        ))}
                    </div>
                )}
                {refundForm && (
                    <div className="flex flex-col gap-2 border-t border-mono-100 pt-3 dark:border-mono-900">
                        <Select
                            label="Situação"
                            value={refundForm.status}
                            onChange={(e) => setRefundForm((f) => ({ ...f, status: e.target.value }))}
                        >
                            {Object.entries(REFUND_LABELS).map(([value, text]) => (
                                <option key={value} value={value}>
                                    {text}
                                </option>
                            ))}
                        </Select>
                        <div className="grid grid-cols-2 gap-2">
                            <Input
                                label="Valor (R$)"
                                type="number"
                                step="0.01"
                                value={refundForm.amount}
                                onChange={(e) => setRefundForm((f) => ({ ...f, amount: e.target.value }))}
                            />
                            <Select
                                label="Canal"
                                value={refundForm.channel}
                                onChange={(e) => setRefundForm((f) => ({ ...f, channel: e.target.value }))}
                            >
                                <option value="">—</option>
                                <option value="ML">Pelo ML</option>
                                <option value="POR_FORA">Por fora (Pix)</option>
                            </Select>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" onClick={saveRefund} loading={busy}>
                                Salvar
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRefundForm(null)}>
                                Cancelar
                            </Button>
                        </div>
                    </div>
                )}
                <div className="flex flex-wrap gap-2">
                    {canFinance && needsReconcile && !nextAction?.finance && (
                        <Button size="sm" variant="secondary" onClick={reconcile} disabled={busy}>
                            Marcar conciliado/verificado
                        </Button>
                    )}
                    {/* NF de perda (rodada 7): "chegou errado" (inspeção com problema)
                        ou destino DESCARTE (supervisor); o reembolsado continua
                        podendo marcar, como antes. */}
                    {!rma.nf_loss_at &&
                        (canFinance || canLogistics) &&
                        (rma.inspection_issue === true ||
                            rma.inspection_destination === 'DESCARTE' ||
                            ['REEMBOLSADO', 'PARCIAL'].includes(rma.refund_status)) && (
                            <Button size="sm" variant="ghost" onClick={() => markFiscal('PERDA')} disabled={busy}>
                                Marcar NF de perda
                            </Button>
                        )}
                </div>
            </Panel>
        </div>
    );
    // Embutida (rodada 7 — spec-fiscal/spec-nf-xml): as NFs do caso na seção
    // Devolução — NF-e de devolução (emitida pelo ML, com DANFE, ou a nossa)
    // e NF de perda (só quando a devolução chegou ERRADA). Marcar = subir o
    // XML da NF-e, travado por rmas:finance:update (`CaseFiscalAction`).
    const fiscalEntryShown = IsReturnToUs(fiscalRma) || HasMlReturnInvoice(fiscalRma) || Boolean(rma.nf_entry_at);
    // Devolução cancelada/expirada sem NF nenhuma: nada a emitir (texto
    // neutro, sem pendência — como a tag nfe_devolucao_para_emitir).
    const fiscalEntrySkipped = IsReturnSkipped(fiscalRma) && !HasMlReturnInvoice(fiscalRma) && !rma.nf_entry_at;
    // NF de perda: "chegou errado" (`NeedsLossNf`) OU o supervisor definiu o
    // destino DESCARTE (ajuste 2 do dono — mesma regra do backend,
    // fiscal.nf_perda_pendente / tag nf_perda_para_emitir).
    const fiscalLossNeeded = NeedsLossNf(fiscalRma) || (rma.inspection_destination === 'DESCARTE' && !rma.nf_loss_at);
    const fiscalLossShown = fiscalLossNeeded || Boolean(rma.nf_loss_at);
    const fiscalLabelClass = 'text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600';
    const fiscalCard =
        embedded && (fiscalEntryShown || fiscalLossShown) ? (
            <Panel
                key="fiscal"
                title="Notas fiscais"
                action={
                    <span className="text-[11px] text-mono-400 dark:text-mono-500">
                        {canFinance ? 'marcar emitida = subir o XML da NF-e' : 'marcar exige rmas:finance:update'}
                    </span>
                }
            >
                <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                    <div className="min-w-0">
                        <p className={fiscalLabelClass}>NF-e de devolução</p>
                        {fiscalEntryShown && fiscalEntrySkipped ? (
                            <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                — devolução não aconteceu (cancelada/expirada)
                            </span>
                        ) : fiscalEntryShown ? (
                            <CaseFiscalAction
                                occ={fiscalRma}
                                type="ENTRADA"
                                needed={NeedsReturnNfe(fiscalRma)}
                                canFinance={canFinance}
                                toast={toast}
                                onMarked={reloadAfterChange}
                            />
                        ) : (
                            <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                — sem devolução para o nosso galpão
                            </span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className={fiscalLabelClass}>NF de perda</p>
                        {fiscalLossShown ? (
                            <CaseFiscalAction
                                occ={fiscalRma}
                                type="PERDA"
                                needed={fiscalLossNeeded}
                                canFinance={canFinance}
                                toast={toast}
                                onMarked={reloadAfterChange}
                            />
                        ) : (
                            <span className="text-[12px] text-mono-400 dark:text-mono-500">
                                — só se a devolução chegar errada
                            </span>
                        )}
                    </div>
                </div>
            </Panel>
        ) : null;
    // Anexos que não são foto da inspeção (essas vão agrupadas por etapa).
    const otherAttachments = photoGroups.outros;
    const imagens = otherAttachments.filter((a) => /\.(jpe?g|png|webp|gif)$/i.test(a.attachment_key || ''));
    const stepPhotoCount = attachments.length - otherAttachments.length;
    const eventosDe = (tipos) => (detail.events || []).filter((e) => tipos.includes(e.type));

    return (
        <div className={embedded ? 'flex w-full flex-col gap-2' : 'mx-auto flex max-w-6xl flex-col gap-4'}>
            {!embedded && (
                <div>
                    <button
                        type="button"
                        onClick={leave}
                        className="flex items-center gap-1 px-2 py-1 text-sm text-mono-500 transition-colors hover:bg-mono-100 hover:text-black dark:hover:bg-mono-900 dark:hover:text-white"
                    >
                        <ArrowLeft size={15} /> Pós-venda
                    </button>
                </div>
            )}

            {/* ── Cabeçalho compacto (redesign 2026-08-26): 2 linhas ── */}
            {/* Embutida: quem embute já mostra identidade, pill, trilhos e prazo. */}
            {!embedded && (
                <Panel>
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
                        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                            <h1 className="font-mono text-lg font-bold text-black dark:text-white">
                                {rmaIdentifier(rma)}
                            </h1>
                            <StatusPill status={rma.status} />
                            <TrackChips rma={trackRma} />
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                            {due && (
                                <span className={`text-xs font-semibold ${due.tone}`}>
                                    {due.label}
                                    {rma.has_incentive ? ' · prazo ML 48h' : ''}
                                </span>
                            )}
                            {canResolve && (
                                <button
                                    type="button"
                                    onClick={() => setAdminEditOpen(true)}
                                    className=" border border-mono-300 px-3 py-1 text-xs font-semibold text-mono-600 transition-colors hover:border-black hover:text-black dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white"
                                >
                                    Editar
                                </button>
                            )}
                        </div>
                    </div>
                    <p className="truncate text-sm text-mono-600 dark:text-mono-300">
                        {rma.item_title || 'Produto sem título'}
                        {rma.quantity > 1 ? ` · ${rma.quantity} un` : ''}
                        <span className="text-mono-400 dark:text-mono-500"> — {SituationLine(rma, tracks)}</span>
                    </p>
                </Panel>
            )}

            {/* ── Próxima ação: o passo mais urgente entre os processos ── */}
            {(nextAction || nextInboxId) && (
                <div
                    className={`flex flex-wrap items-center gap-3 px-5 py-4 ${
                        nextAction?.doneAll
                            ? 'bg-emerald-600 text-white dark:bg-emerald-500'
                            : nextAction?.urgent
                              ? 'bg-danger-600 text-white dark:bg-danger-500'
                              : nextAction?.finance
                                ? 'bg-cyan-700 text-white dark:bg-cyan-600'
                                : 'bg-black text-white dark:bg-white dark:text-black'
                    }`}
                >
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">Próxima ação</p>
                        <p className="text-base font-semibold leading-snug">
                            {nextAction?.text || 'Nada pendente aqui — siga a fila'}
                        </p>
                        {nextAction?.hint && <p className="text-xs opacity-80">{nextAction.hint}</p>}
                    </div>
                    {nextInboxId && (
                        <button
                            type="button"
                            onClick={() => navigate(`/rmas/${nextInboxId}`, { state: { inboxIds } })}
                            className="shrink-0 border border-white/40 px-4 py-2.5 text-sm font-semibold opacity-90 transition-opacity hover:opacity-100"
                        >
                            Próximo da caixa →
                        </button>
                    )}
                    {nextAction?.button &&
                        (nextAction.button.href ? (
                            <a
                                href={nextAction.button.href}
                                target="_blank"
                                rel="noreferrer"
                                className="flex shrink-0 items-center gap-1.5 bg-white px-5 py-2.5 text-sm font-bold text-black transition-transform hover:bg-mono-100 active:scale-95"
                            >
                                {nextAction.button.label} <ArrowSquareOut size={15} />
                            </a>
                        ) : (
                            <button
                                type="button"
                                onClick={nextAction.button.onClick}
                                disabled={busy}
                                className="shrink-0 bg-white px-5 py-2.5 text-sm font-bold text-black transition-transform hover:bg-mono-100 active:scale-95 disabled:opacity-60 dark:bg-black dark:text-white dark:hover:bg-mono-900"
                            >
                                {nextAction.button.label}
                            </button>
                        ))}
                </div>
            )}

            <div className={embedded ? 'flex flex-col' : 'grid items-start gap-4 lg:grid-cols-[minmax(0,1fr),18rem]'}>
                <div
                    className={
                        embedded
                            ? 'flex min-w-0 flex-col divide-y divide-mono-200 dark:divide-mono-800'
                            : 'flex min-w-0 flex-col gap-4'
                    }
                >
                    {/* ── Abas: Agora (workspace) e mergulhos por área (tela própria) ── */}
                    {!embedded && (
                        <div className="flex flex-wrap gap-1 border-b border-mono-200 dark:border-mono-800">
                            {visibleTabs.map(([key, label]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setTab(key)}
                                    className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                                        tab === key
                                            ? 'border-black text-black dark:border-white dark:text-white'
                                            : 'border-transparent text-mono-400 hover:text-mono-600 dark:text-mono-500 dark:hover:text-mono-300'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Embutida (rodada 5): caso encerrado não conciliado cuja "Próxima
                        ação" é outra (ex.: devolução ainda no fluxo físico) — a ação
                        de conciliar/verificar fica numa linha própria. */}
                    {embedded && canFinance && needsReconcile && !nextAction?.finance && (
                        <div className="flex flex-wrap items-center justify-between gap-2 border-l-4 border-l-cyan-600 px-5 py-2">
                            <span className="text-xs text-mono-600 dark:text-mono-300">
                                Financeiro: caso encerrado ainda não conciliado/verificado.
                            </span>
                            <button
                                type="button"
                                onClick={reconcile}
                                disabled={busy}
                                className="whitespace-nowrap border border-cyan-700 px-2.5 py-1 text-[12px] font-semibold text-cyan-800 transition-colors hover:bg-cyan-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-cyan-500 dark:text-cyan-300 dark:hover:bg-cyan-950"
                            >
                                Marcar conciliado/verificado
                            </button>
                        </div>
                    )}
                    {/* Embutida: o cabeçalho sumiu, o "Editar" do supervisor mora aqui. */}
                    {embedded && canResolve && (
                        <div className="flex justify-end px-5 py-2">
                            <button
                                type="button"
                                onClick={() => setAdminEditOpen(true)}
                                className="border border-mono-300 px-3 py-1 text-xs font-semibold text-mono-600 transition-colors hover:border-black hover:text-black dark:border-mono-700 dark:text-mono-300 dark:hover:border-white dark:hover:text-white"
                            >
                                Editar
                            </button>
                        </div>
                    )}

                    {(embedded || tab === 'geral') && (
                        <div className={embedded ? EMBEDDED_STACK_CLASS : 'flex flex-col gap-3'}>
                            {!tracks.ml.na && (
                                <TrackSection
                                    flat={embedded}
                                    open={isOpenTrack('ml')}
                                    onToggle={() => toggleTrack('ml')}
                                    title="Mercado Livre"
                                    summary={`${tracks.ml.label}${
                                        due && !(due.closed && tracks.ml.done) ? ` · ${due.label}` : ''
                                    }`}
                                >
                                    {mlCard}
                                </TrackSection>
                            )}
                            {/* Embutida: Galpão e Análise do ML moram na seção "Devolução" abaixo. */}
                            {!embedded && !tracks.fisico.na && (
                                <TrackSection
                                    flat={embedded}
                                    open={isOpenTrack('fisico')}
                                    onToggle={() => toggleTrack('fisico')}
                                    title="Galpão"
                                    summary={`${tracks.fisico.label}${rma.tracking_number ? ` · rastreio ${rma.tracking_number}` : ''}`}
                                >
                                    <div className="flex flex-col gap-3">
                                        {galpaoCard}
                                        {destinoCard}
                                    </div>
                                </TrackSection>
                            )}
                            {/* Sem trilho físico ativo, o "Definir destino" de um caso
                                INSPECIONADO não some junto com a seção Galpão. */}
                            {!embedded && tracks.fisico.na && destinoCard}
                            {!embedded && (detail.return_reviews || []).length > 0 && (
                                <TrackSection
                                    flat={embedded}
                                    open={isOpenTrack('analise')}
                                    onToggle={() => toggleTrack('analise')}
                                    title="Análise do ML"
                                    summary="revisão da devolução feita pelo Mercado Livre"
                                >
                                    {mlReviewCard}
                                </TrackSection>
                            )}
                            {!tracks.fabricante.na && (
                                <TrackSection
                                    flat={embedded}
                                    open={isOpenTrack('fabricante')}
                                    onToggle={() => toggleTrack('fabricante')}
                                    title="Fabricante"
                                    summary={tracks.fabricante.label}
                                >
                                    {fabricanteCard}
                                </TrackSection>
                            )}
                            {/* Embutida: o reembolso do caso mora na aba Financeiro do pedido
                                (gated por payments:read; ações por rmas:finance:update). */}
                            {!embedded && (
                                <TrackSection
                                    flat={embedded}
                                    open={isOpenTrack('financeiro')}
                                    onToggle={() => toggleTrack('financeiro')}
                                    title="Financeiro"
                                    summary={`${tracks.financeiro.label}${
                                        rma.refund_amount !== null
                                            ? ` · R$ ${Number(rma.refund_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                            : ''
                                    }`}
                                >
                                    {financeiroCard}
                                </TrackSection>
                            )}
                            {!embedded && (
                                <TrackSection
                                    open={isOpenTrack('notas')}
                                    onToggle={() => toggleTrack('notas')}
                                    title="Notas & histórico"
                                    summary={`${(detail.events || []).length} eventos${
                                        (detail.events || []).length
                                            ? ` · último ${timeAgo(
                                                  (detail.events || []).reduce((max, e) =>
                                                      new Date(e.created_at) > new Date(max.created_at) ? e : max,
                                                  ).created_at,
                                              )}`
                                            : ''
                                    }`}
                                >
                                    {notasCard}
                                </TrackSection>
                            )}
                            {!isTerminal && canResolve && (
                                <div className={embedded ? 'flex justify-end px-5 py-2' : 'flex justify-end'}>
                                    <button
                                        type="button"
                                        onClick={() => transition('CANCELADO')}
                                        disabled={busy}
                                        className="text-xs text-mono-400 underline underline-offset-2 hover:text-danger-600"
                                    >
                                        Encerrar sem desfecho
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {!embedded && tab === 'venda' && (
                        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
                            <Panel
                                title="Venda no Mercado Livre"
                                action={
                                    rma.external_order_id ? (
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/payments/order/${rma.external_order_id}`)}
                                            className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                        >
                                            ver em Pagamentos
                                        </button>
                                    ) : null
                                }
                            >
                                <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
                                    <Fact label="Pedido" mono>
                                        {rma.external_order_id}
                                    </Fact>
                                    <Fact label="Data da venda">{fmtDate(detail.sale?.date_created)}</Fact>
                                    <Fact label="Valor">
                                        {detail.sale?.total_amount !== null && detail.sale?.total_amount !== undefined
                                            ? `R$ ${Number(detail.sale.total_amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                            : null}
                                    </Fact>
                                    <Fact label="Comprador">{rma.buyer_nickname}</Fact>
                                    <Fact label="SKU" mono>
                                        {rma.sku}
                                    </Fact>
                                    <Fact label="Quantidade">{String(rma.quantity || 1)}</Fact>
                                    <Fact label="Logística">
                                        {detail.sale?.shipping_logistic_type
                                            ? MlLabel(ML_LOGISTIC_TYPE_LABELS, detail.sale.shipping_logistic_type)
                                            : null}
                                    </Fact>
                                    <Fact label="Status do envio">
                                        {detail.sale?.shipping_status
                                            ? MlLabel(ML_SHIPPING_STATUS_LABELS, detail.sale.shipping_status)
                                            : null}
                                    </Fact>
                                    <Fact label="Destino">
                                        {detail.sale?.shipping_city
                                            ? `${detail.sale.shipping_city} · ${detail.sale.shipping_state || ''}`
                                            : null}
                                    </Fact>
                                </div>
                                {!detail.sale && (
                                    <p className="text-xs text-mono-400">Venda não encontrada na base local.</p>
                                )}
                                {detail.sale_invoice && (
                                    <div className="flex flex-col gap-1.5 border-t border-mono-100 pt-2 dark:border-mono-900">
                                        <div className="flex items-center justify-between gap-3">
                                            <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                                                NF-e da venda (faturador ML)
                                            </p>
                                            <a
                                                href={DanfeUrl(rma.id)}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                            >
                                                baixar DANFE
                                            </a>
                                        </div>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-4">
                                            <Fact label="Número / série">
                                                {detail.sale_invoice.invoice_number
                                                    ? `${detail.sale_invoice.invoice_number} / ${detail.sale_invoice.invoice_series}`
                                                    : null}
                                            </Fact>
                                            <Fact label="Valor">
                                                {detail.sale_invoice.amount !== null
                                                    ? `R$ ${detail.sale_invoice.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                                    : null}
                                            </Fact>
                                            <Fact label="Situação">
                                                {detail.sale_invoice.status
                                                    ? MlLabel(ML_INVOICE_STATUS_LABELS, detail.sale_invoice.status)
                                                    : null}
                                            </Fact>
                                            <Fact label="Emitida em">{fmtDate(detail.sale_invoice.issued_date)}</Fact>
                                        </div>
                                        {detail.sale_invoice.invoice_key && (
                                            <p className="font-mono text-[11px] text-mono-400 dark:text-mono-600">
                                                chave: {detail.sale_invoice.invoice_key}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </Panel>
                            {mlCard}
                        </div>
                    )}

                    {(embedded ? hasReturn : tab === 'inspecao') && (
                        <div
                            className={embedded ? EMBEDDED_STACK_CLASS : 'mx-auto flex w-full max-w-3xl flex-col gap-4'}
                        >
                            {embedded && (
                                <h3 className="mx-5 mt-4 flex items-center gap-1.5 pb-1 text-xs font-bold uppercase tracking-widest text-mono-500 dark:text-mono-400">
                                    <span className="h-2 w-2 bg-amber-500" /> Devolução
                                </h3>
                            )}
                            {galpaoCard}
                            {destinoCard}
                            {fiscalCard}
                            {mlReviewCard}
                            <Panel title={`Fotos e anexos (${attachments.length})`}>
                                {/* Fotos da inspeção por etapa; com o bloco "Definir
                                    destino" aberto (supervisor), elas moram lá. */}
                                {destinoVisible ? (
                                    stepPhotoCount > 0 && (
                                        <p className="text-xs text-mono-500 dark:text-mono-400">
                                            {CountLabel(stepPhotoCount, 'foto')} da inspeção — por etapa, no bloco
                                            “Definir destino”.
                                        </p>
                                    )
                                ) : (
                                    <InspectionPhotoGroups groups={photoGroups} />
                                )}
                                {imagens.length > 0 && stepPhotoCount > 0 && (
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-mono-400 dark:text-mono-600">
                                        Outros anexos
                                    </p>
                                )}
                                {imagens.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {imagens.map((a) => (
                                            <a
                                                key={a.id}
                                                href={ResolveAssetUrl(a.attachment_key)}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <img
                                                    src={ResolveAssetUrl(a.attachment_key)}
                                                    alt={a.attachment_name || 'foto da inspeção'}
                                                    loading="lazy"
                                                    className="h-28 w-28 border border-mono-200 object-cover transition-transform hover:scale-105 dark:border-mono-800"
                                                />
                                            </a>
                                        ))}
                                    </div>
                                )}
                                {otherAttachments.length > imagens.length && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {otherAttachments
                                            .filter((a) => !imagens.includes(a))
                                            .map((a) => (
                                                <a
                                                    key={a.id}
                                                    href={ResolveAssetUrl(a.attachment_key)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className=" bg-mono-100 px-2 py-1 text-xs underline-offset-2 hover:underline dark:bg-mono-900"
                                                >
                                                    📎 {a.attachment_name || 'anexo'}
                                                </a>
                                            ))}
                                    </div>
                                )}
                                {attachments.length === 0 && (
                                    <p className="text-sm text-mono-400">
                                        Nenhuma foto ou anexo ainda — eles entram na inspeção.
                                    </p>
                                )}
                            </Panel>
                        </div>
                    )}

                    {!embedded && tab === 'garantia' && (
                        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
                            {supplierApplies ? (
                                <>
                                    {fabricanteCard}
                                    <Panel title="Histórico com o fabricante">
                                        {eventosDe(['FORNECEDOR']).length === 0 && (
                                            <p className="text-sm text-mono-400">Nenhum registro ainda.</p>
                                        )}
                                        {eventosDe(['FORNECEDOR']).map((e) => (
                                            <p key={e.id} className="text-sm text-mono-600 dark:text-mono-300">
                                                <span className="text-[11px] text-mono-400">
                                                    {fmtDate(e.created_at)}
                                                </span>{' '}
                                                — {e.to_value}
                                                {e.note ? ` · ${e.note}` : ''}
                                            </p>
                                        ))}
                                    </Panel>
                                </>
                            ) : (
                                <Panel title="Garantia — RMA com o fabricante">
                                    <p className="text-sm text-mono-500">
                                        Este RMA não tem processo de garantia: o trilho do fabricante nasce quando o
                                        desfecho da inspeção é <strong>Devolução ao fornecedor</strong> ou{' '}
                                        <strong>Garantia (fabricante)</strong>.
                                    </p>
                                </Panel>
                            )}
                        </div>
                    )}

                    {!embedded && tab === 'financeiro' && (
                        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
                            {financeiroCard}
                            <Panel title="Histórico financeiro">
                                {eventosDe(['REEMBOLSO', 'FISCAL']).length === 0 && (
                                    <p className="text-sm text-mono-400">Nenhum registro ainda.</p>
                                )}
                                {eventosDe(['REEMBOLSO', 'FISCAL']).map((e) => (
                                    <p key={e.id} className="text-sm text-mono-600 dark:text-mono-300">
                                        <span className="text-[11px] text-mono-400">{fmtDate(e.created_at)}</span> —{' '}
                                        {e.type} {e.to_value}
                                        {e.note ? ` · ${e.note}` : ''}
                                    </p>
                                ))}
                            </Panel>
                        </div>
                    )}
                </div>

                {/* ── Rail de contexto (só leitura): identidade, irmãs, atalhos ── */}
                {/* Embutida: quem embute já mostra identidade e atalhos — sem rail. */}
                {!embedded && (
                    <aside className="flex flex-col gap-3">
                        <Panel title="Contexto">
                            <div className="flex flex-col gap-2.5">
                                <Fact label="SKU" mono>
                                    {rma.sku}
                                </Fact>
                                <Fact label="Pedido" mono>
                                    {rma.external_order_id}
                                </Fact>
                                <Fact label="Comprador">{rma.buyer_nickname}</Fact>
                                <Fact label="Motivo">{rma.reason_detail || rma.reason_id}</Fact>
                                {rma.serial_number && (
                                    <Fact label="Nº de série" mono>
                                        {rma.serial_number}
                                    </Fact>
                                )}
                                <Fact label="Aberto em">{fmtDate(rma.claim_date_created || rma.created_at)}</Fact>
                                <Fact label="Origem">{rma.source === 'ML' ? 'Mercado Livre' : 'Manual'}</Fact>
                            </div>
                        </Panel>
                        {(detail.related_rmas || []).length > 0 && (
                            <Panel title={`Outras do pedido (${detail.related_rmas.length})`}>
                                {detail.related_rmas.map((sib) => (
                                    <button
                                        key={sib.id}
                                        type="button"
                                        onClick={() => navigate(`/rmas/${sib.id}`)}
                                        className="text-left font-mono text-xs font-semibold text-warning-800 underline underline-offset-2 hover:text-black dark:text-warning-300 dark:hover:text-white"
                                    >
                                        #{sib.external_claim_id || sib.id.slice(-8)} ·{' '}
                                        {sib.mediation_status === 'GANHA'
                                            ? 'ganha'
                                            : sib.mediation_status === 'PERDIDA'
                                              ? 'perdida'
                                              : sib.mediation_status === 'EM_MEDIACAO'
                                                ? 'em mediação'
                                                : sib.ml_resolution_reason || sib.status === 'CANCELADO'
                                                  ? 'encerrada'
                                                  : 'em curso'}
                                    </button>
                                ))}
                            </Panel>
                        )}
                        <Panel title="Atalhos">
                            <div className="flex flex-col gap-1.5 text-xs font-semibold">
                                {rma.source === 'ML' && (
                                    <a
                                        href={MlClaimUrl(rma, packId)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-mono-600 underline underline-offset-2 hover:text-black dark:text-mono-300 dark:hover:text-white"
                                    >
                                        Abrir no painel do ML
                                    </a>
                                )}
                                {rma.external_order_id && (
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/payments/order/${rma.external_order_id}`)}
                                        className="text-left text-mono-600 underline underline-offset-2 hover:text-black dark:text-mono-300 dark:hover:text-white"
                                    >
                                        Ver em Pagamentos
                                    </button>
                                )}
                            </div>
                        </Panel>
                    </aside>
                )}
            </div>

            {resolveOpen && (
                <ResolveModal
                    isOpen={resolveOpen}
                    onClose={() => setResolveOpen(false)}
                    saving={busy}
                    title={
                        resolveDestination
                            ? `Destinar: ${InspectionDestinationMeta(resolveDestination)?.label}`
                            : undefined
                    }
                    intro={resolveDestination ? DESTINATION_RESOLVE_INTRO[resolveDestination] || null : null}
                    allowedResolutions={resolveDestination ? DESTINATION_RESOLUTIONS[resolveDestination] || null : null}
                    defaultResolution={
                        resolveDestination
                            ? DESTINATION_RESOLUTIONS[resolveDestination]?.[0] || 'REESTOQUE'
                            : rma.suggested_resolution || 'REESTOQUE'
                    }
                    onConfirm={({ resolution, note: resolveNote }) =>
                        transition('RESOLVIDO', { resolution, note: resolveNote || undefined })
                    }
                />
            )}
            {/* Inspeção: o assistente do checklist em tela cheia (o mesmo da
                estação de bipe); ao registrar, a tela "Inspeção concluída". */}
            {inspectOpen && (
                <InspectionScreen>
                    {inspectDone ? (
                        <InspectionDoneCard
                            rma={rma}
                            result={inspectDone}
                            action={{ label: 'Fechar', onClick: closeInspection }}
                        />
                    ) : (
                        <InspectionWizard
                            key={rma.id}
                            rma={rma}
                            saving={busy}
                            onCancel={closeInspection}
                            onConfirm={confirmInspection}
                        />
                    )}
                </InspectionScreen>
            )}
            {adminEditOpen && (
                <AdminEditModal
                    isOpen={adminEditOpen}
                    onClose={() => setAdminEditOpen(false)}
                    saving={busy}
                    rma={rma}
                    onConfirm={adminSave}
                />
            )}
            {labelConfirmOpen && (
                <Modal
                    isOpen={labelConfirmOpen}
                    onClose={() => setLabelConfirmOpen(false)}
                    title="A devolução chegou ao galpão?"
                    size="sm"
                >
                    <div className="flex flex-col gap-3">
                        {labelConfirmReason ? (
                            <p className="bg-warning-50 p-2.5 text-sm text-warning-800 dark:bg-warning-400/10 dark:text-warning-300">
                                {labelConfirmReason}
                            </p>
                        ) : (
                            <p className="text-sm text-mono-600 dark:text-mono-300">
                                O Mercado Livre ainda não marcou esta devolução como entregue.
                            </p>
                        )}
                        <p className="text-sm text-mono-600 dark:text-mono-300">
                            Só imprima com a mercadoria em mãos: a etiqueta registra o recebimento no galpão e manda o
                            caso para a inspeção do Atendimento.
                        </p>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => setLabelConfirmOpen(false)}>
                                Não, ainda não chegou
                            </Button>
                            <Button loading={busy} onClick={() => generateCaseLabels(true)}>
                                Sim, chegou — imprimir
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
            {supplierConcludeOpen && (
                <SupplierConcludeModal
                    isOpen={supplierConcludeOpen}
                    onClose={() => setSupplierConcludeOpen(false)}
                    saving={busy}
                    onConfirm={concludeSupplier}
                />
            )}
        </div>
    );
};

export default RmaDetail;
